import React, { useState } from "react";

import classNames from "classnames";
import { capitalize } from "lodash";
import {
    Button,
    ButtonGroup,
    CloseButton,
    Col,
    Form,
    FormCheck,
    FormGroup,
    Modal,
    Row,
} from "react-bootstrap";
import Select from "react-select";

import { useBookingModalContext } from "../contexts/Modals";
import { useGamesContext } from "../contexts/Games";
import { useButtonHandlers } from "../hooks/useButtonHandlers";
import { dateFormat } from "../lib/dateFormat";

import Loading from "../../../../components/Loading";
import { defaultTimeFormat } from "../../../../lib/utils";
import { useAppSelector } from "../../../../hooks/reduxHooks";
import { selectUmpires } from "../../../../features/global/globalSlice";

const BookingModal = () => {
    const {
        showBookingModal,
        setShowBookingModal,
        bookingModalContent,
        bookingStatus,
        bookingStatusMessage,
        setBookingStatusMessage,
        setBookingStatus,
    } = useBookingModalContext();

    const { site } = useGamesContext();
    const { handleBookingConfirmClick } = useButtonHandlers();
    const { game, location, umpire, pendingReplacementUmpire } =
        bookingModalContent ?? {};
    const umpires = useAppSelector(selectUmpires);
    const [removeUmpire, setRemoveUmpire] = useState<number | undefined>(
        undefined,
    );
    const [postForReplacement, setPostForReplacement] =
        useState<boolean>(false);
    const [cancelReplacement, setCancelReplacement] = useState<boolean>(false);
    const [newUmpire, setNewUmpire] = useState<number | undefined>(undefined);
    const [overrideDoubleBooking, setOverrideDoubleBooking] =
        useState<boolean>(false);

    const handleClose = () => {
        setBookingStatus(undefined);
        setShowBookingModal(false);
        setBookingStatusMessage(undefined);
        setRemoveUmpire(undefined);
        setNewUmpire(undefined);
    };

    return (
        <Modal
            className={classNames("bookingModal")}
            centered
            show={showBookingModal}
            onHide={handleClose}
        >
            <Modal.Header>
                <div className={classNames("flex-column")}>
                    <h4 className={classNames("text-left")}>
                        Manage{" "}
                        {site === 1
                            ? `${capitalize(location)} Umpire`
                            : location === "plate"
                              ? `Umpire 1`
                              : `Umpire 2`}{" "}
                        : {umpire ? umpire.name : "Available"}
                    </h4>
                    {pendingReplacementUmpire && (
                        <h5 className={classNames("mt-1", "mb-0")}>
                            Pending Replacement by:{" "}
                            {pendingReplacementUmpire.name}
                        </h5>
                    )}
                </div>
                <CloseButton onClick={handleClose} />
            </Modal.Header>
            <Modal.Body>
                {game && (
                    <>
                        <h5 className={classNames("text-center")}>
                            Game Info:
                        </h5>
                        <dl className={classNames("inline", "mb-0")}>
                            <dt>Date:</dt>
                            <dd>{dateFormat.format(new Date(game.date))}</dd>
                            <dt>Time:</dt>
                            <dd>
                                {defaultTimeFormat.format(new Date(game.date))}
                            </dd>
                            <dt>Age Level:</dt>
                            <dd>{game.ageLevel.ageLevel}</dd>
                            <dt>{site === 1 ? "League:" : "Tournament:"}</dt>
                            <dd>{game.leagueName}</dd>
                            {site === 1 && (
                                <>
                                    <dt>Teams:</dt>
                                    <dd>
                                        {`${game.visitor.name || "TBD"} @ ${game.home.name || "TBD"}`}
                                    </dd>
                                </>
                            )}
                            <dt>Location:</dt>
                            <dd>{game.fieldName}</dd>
                            {game.diamond && (
                                <>
                                    <dt>Diamond:</dt>
                                    <dd>{game.diamond}</dd>
                                </>
                            )}
                            {game.note && (
                                <>
                                    <dt>Game/Field Note:</dt>
                                    <dd>{game.note}</dd>
                                </>
                            )}
                        </dl>
                    </>
                )}
                {umpire && (
                    <>
                        <hr />
                        <Row>
                            <Col sm={pendingReplacementUmpire ? 6 : 12} xs={12}>
                                <h5 className={classNames("text-center")}>
                                    Current {capitalize(location)} Umpire Info:
                                </h5>
                                {umpire.name}
                                <br />
                                {umpire.phone}
                                <br />
                                {umpire.email && (
                                    <a href={`mailto:${umpire.email}`}>
                                        {umpire.email}
                                    </a>
                                )}
                            </Col>

                            {pendingReplacementUmpire && (
                                <Col
                                    sm={pendingReplacementUmpire ? 6 : 12}
                                    xs={12}
                                >
                                    <h5 className={classNames("text-center")}>
                                        Pending Replacement Info:
                                    </h5>
                                    {pendingReplacementUmpire.name}
                                    <br />
                                    {pendingReplacementUmpire.phone}
                                    <br />
                                    {pendingReplacementUmpire.email && (
                                        <a
                                            href={`mailto:${pendingReplacementUmpire.email}`}
                                        >
                                            {pendingReplacementUmpire.email}
                                        </a>
                                    )}
                                </Col>
                            )}
                        </Row>
                    </>
                )}
                <hr />
                <h6 className={classNames("text-center")}>
                    Select the umpire you want to{" "}
                    {umpire
                        ? `replace the current umpire with`
                        : `assign to the above game`}
                    .
                </h6>
                <Select
                    isSearchable={true}
                    isMulti={false}
                    isClearable={true}
                    options={umpires
                        .filter(u => u.id !== umpire?.id)
                        .map(u => ({
                            value: u.id,
                            label: u.name as string,
                        }))}
                    defaultValue={null}
                    value={
                        newUmpire
                            ? {
                                  value: newUmpire,
                                  label: umpires.find(u => u.id === newUmpire)
                                      ?.name as string,
                              }
                            : null
                    }
                    onChange={u => {
                        setNewUmpire(u?.value);
                        setBookingStatus(undefined);
                        setBookingStatusMessage(undefined);
                        u && umpire
                            ? setRemoveUmpire(umpire.id)
                            : setRemoveUmpire(undefined);
                    }}
                />
                {newUmpire && (
                    <>
                        <hr />
                        <Row>
                            <Col>
                                <FormGroup
                                    className={classNames("mb-0", "form-group")}
                                >
                                    <FormCheck
                                        id="overrideDoubleBooking"
                                        label="Override Double Booking Prevention?"
                                        checked={overrideDoubleBooking}
                                        onChange={e =>
                                            setOverrideDoubleBooking(
                                                e.currentTarget.checked,
                                            )
                                        }
                                    />
                                </FormGroup>
                            </Col>
                        </Row>
                    </>
                )}
                {umpire && !newUmpire ? (
                    <>
                        <hr />
                        <Row>
                            <Col
                                xs={6}
                                className={classNames(
                                    "d-flex",
                                    "justify-content-center",
                                    "flex-column",
                                )}
                            >
                                <h6 className={classNames("text-center")}>
                                    Remove Current {capitalize(location)} Umpire
                                    ({umpire.name})?
                                </h6>
                                {pendingReplacementUmpire && (
                                    <>
                                        <small>
                                            Note: This will only remove the
                                            current umpire, not the pending
                                            replacement
                                        </small>
                                    </>
                                )}
                                <ButtonGroup className={classNames("mx-auto")}>
                                    <Button
                                        variant={
                                            removeUmpire
                                                ? "outline-primary"
                                                : "primary"
                                        }
                                        onClick={() => {
                                            setRemoveUmpire(undefined);
                                            setBookingStatus(undefined);
                                            setBookingStatusMessage(undefined);
                                        }}
                                    >
                                        No
                                    </Button>
                                    <Button
                                        variant={
                                            removeUmpire
                                                ? "primary"
                                                : "outline-primary"
                                        }
                                        onClick={() => {
                                            setRemoveUmpire(umpire.id);
                                            setBookingStatus(undefined);
                                            setBookingStatusMessage(undefined);
                                        }}
                                    >
                                        Yes
                                    </Button>
                                </ButtonGroup>
                            </Col>
                            {umpire &&
                                !newUmpire &&
                                !removeUmpire &&
                                (!umpire.replacement ? (
                                    <Col
                                        xs={6}
                                        className={classNames(
                                            "d-flex",
                                            "justify-content-center",
                                            "flex-column",
                                        )}
                                    >
                                        <h6
                                            className={classNames(
                                                "text-center",
                                            )}
                                        >
                                            Post this game for replacement?
                                        </h6>
                                        <ButtonGroup
                                            className={classNames("mx-auto")}
                                        >
                                            <Button
                                                variant={
                                                    postForReplacement
                                                        ? "outline-primary"
                                                        : "primary"
                                                }
                                                onClick={() => {
                                                    setPostForReplacement(
                                                        false,
                                                    );
                                                    setBookingStatus(undefined);
                                                    setBookingStatusMessage(
                                                        undefined,
                                                    );
                                                }}
                                            >
                                                No
                                            </Button>
                                            <Button
                                                variant={
                                                    postForReplacement
                                                        ? "primary"
                                                        : "outline-primary"
                                                }
                                                onClick={() => {
                                                    setPostForReplacement(true);
                                                    setBookingStatus(undefined);
                                                    setBookingStatusMessage(
                                                        undefined,
                                                    );
                                                }}
                                            >
                                                Yes
                                            </Button>
                                        </ButtonGroup>
                                    </Col>
                                ) : (
                                    <Col
                                        xs={6}
                                        className={classNames(
                                            "d-flex",
                                            "justify-content-center",
                                            "flex-column",
                                        )}
                                    >
                                        <h6
                                            className={classNames(
                                                "text-center",
                                            )}
                                        >
                                            Cancel this replacement request?
                                        </h6>
                                        {pendingReplacementUmpire && (
                                            <>
                                                <small>
                                                    Note: This will also cancel
                                                    the pending replacement
                                                </small>
                                            </>
                                        )}
                                        <ButtonGroup
                                            className={classNames("mx-auto")}
                                        >
                                            <Button
                                                variant={
                                                    cancelReplacement
                                                        ? "outline-primary"
                                                        : "primary"
                                                }
                                                onClick={() => {
                                                    setCancelReplacement(false);
                                                    setBookingStatus(undefined);
                                                    setBookingStatusMessage(
                                                        undefined,
                                                    );
                                                }}
                                            >
                                                No
                                            </Button>
                                            <Button
                                                variant={
                                                    cancelReplacement
                                                        ? "primary"
                                                        : "outline-primary"
                                                }
                                                onClick={() => {
                                                    setCancelReplacement(true);
                                                    setBookingStatus(undefined);
                                                    setBookingStatusMessage(
                                                        undefined,
                                                    );
                                                }}
                                            >
                                                Yes
                                            </Button>
                                        </ButtonGroup>
                                    </Col>
                                ))}
                        </Row>
                    </>
                ) : pendingReplacementUmpire ? (
                    <>
                        <small>
                            Note: Replacing the current umpire will also cancel
                            the pending replacement
                        </small>
                    </>
                ) : (
                    ""
                )}
                {bookingStatus === "pending" && <Loading />}
                <hr />
                <h4
                    className={classNames({
                        statusMessage: true,
                        bookingStatus: true,
                        "text-primary": bookingStatus === "success",
                        "text-danger": bookingStatus === "failed",
                        "d-none": !bookingStatus || bookingStatus === "pending",
                    })}
                >
                    {bookingStatusMessage}
                </h4>
            </Modal.Body>
            <Modal.Footer>
                <Button
                    variant="danger"
                    onClick={handleClose}
                    disabled={bookingStatus === "pending"}
                >
                    Close
                </Button>
                <Button
                    variant="primary"
                    disabled={bookingStatus === "pending"}
                    onClick={() =>
                        game &&
                        location &&
                        handleBookingConfirmClick(
                            game,
                            location,
                            newUmpire,
                            removeUmpire,
                            postForReplacement,
                            cancelReplacement,
                            umpire,
                            overrideDoubleBooking,
                        )
                            .then(() => {
                                setNewUmpire(undefined);
                                setRemoveUmpire(undefined);
                                setPostForReplacement(false);
                                setCancelReplacement(false);
                                setOverrideDoubleBooking(false);
                            })
                            .catch(e => {})
                    }
                >
                    Save
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

export default BookingModal;
